import { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { formatNumbers, getObjectValue } from 'utilities';
import { ControlProps, NumberMultipleCheckboxProps } from './NumberMultipleCheckbox.props';
import useNumberMultipleCheckboxStyles from './NumberMultipleCheckbox.styles';
import Strike from './Strike';

// isInvalidForCheckbox basically makes the checkbox a no-op for a given cell
// for most cases it's about the numeric value being <= 0, but for investment type companies it also includes "N/A" and infinity
// and for backsolve market adjustment 0 and negative are allowed
const NumberMultipleCheckbox = ({
  cell,
  selected,
  rightAlignContent,
  toggleOnLabelClick,
}: NumberMultipleCheckboxProps) => {
  const labelTogglesCheckbox = toggleOnLabelClick !== false;
  const {
    allowNegativeValue: cellAllowNegativeValue,
    blankIfNull: cellBlankIfNull,
    currency: cellCurrencyRaw,
    currencyCode: cellCurrencyCode,
    data: cellData,
    enabled: cellEnabled,
    expr: cellExpr,
    format: cellFormat,
    isEditableTitleCell,
    prefix: cellPrefix,
    staticCurrency: cellStaticCurrency,
    suffix: cellSuffix,
    value: cellValue,
  } = cell;

  const cellCurrency = cellCurrencyRaw ?? (cell as any).customFormat?.currency;

  const { isInvalidForCheckbox } = getObjectValue(cellData);

  const classes = useNumberMultipleCheckboxStyles({ isEditableTitleCell, rightAlignContent });
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const [enabled, setEnabled] = useState(cellEnabled);

  const changeCell = useCallback(
    (enable: boolean) => {
      cell.setProps?.({ enabled: enable, ignoreExpr: !enable });
      setEnabled(enable);
      onCellsChanged([{ cell, value: cellExpr }]);
    },
    [cell, cellExpr, onCellsChanged]
  );

  useEffect(() => {
    if (!isInvalidForCheckbox) {
      setEnabled(cellEnabled);
    }
  }, [cellEnabled, changeCell, isInvalidForCheckbox]);

  // Function to check if the target is a table cell container or a checkbox
  const isValidTarget = useCallback((target: HTMLInputElement) => {
    const isDataGridContainer = target.tagName === 'SPAN' && target.classList.contains('data-grid-container');
    const isInputCheckbox = target.tagName === 'INPUT' && target.type === 'checkbox';
    return isDataGridContainer || isInputCheckbox;
  }, []);

  const keyUpDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const { code, type, target } = event;

      // If the target is valid
      if (isValidTarget(target as HTMLInputElement)) {
        if (selected && code === 'Space') {
          // Prevent the default spacebar behavior (scroll down)
          event.preventDefault();
          if (type === 'keyup' && !isInvalidForCheckbox) {
            changeCell(!cellEnabled);
          }
        }
      } else {
        // Remove the keyup event to prevent the spacebar from being captured by the component elsewhere
        document.removeEventListener('keyup', keyUpDownHandler);
      }
    },
    [isValidTarget, selected, isInvalidForCheckbox, changeCell, cellEnabled]
  );

  // Click handler to check if the user clicked outside a cell
  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      // If the click did not occur inside a table cell ('td'), remove the keyup event
      if (!target?.closest('td')) {
        document.removeEventListener('keyup', keyUpDownHandler);
      }
    },
    [keyUpDownHandler]
  );

  useEffect(() => {
    if (!selected) return;
    document.addEventListener('keyup', keyUpDownHandler);
    document.addEventListener('keydown', keyUpDownHandler);
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('keyup', keyUpDownHandler);
      document.removeEventListener('keydown', keyUpDownHandler);
      document.removeEventListener('click', handleClick);
    };
  }, [selected, keyUpDownHandler, handleClick]);

  const handleChange = useCallback(() => {
    const newEnabled = !enabled;
    changeCell(newEnabled);
  }, [changeCell, enabled]);

  // Valuations "multiples": clicking (or pressing Enter/Space on) the number/label
  // toggles include/exclude. The label only renders this interactive form while the
  // cell is selected and label-toggling is enabled, so it fires as the original
  // "second click toggles" gesture. stopPropagation keeps it from bubbling to the
  // datasheet (the cell is already selected). An invalid number is never toggled, so
  // the gesture falls through to the datasheet instead.
  const handleLabelClick = useCallback(
    (e: React.MouseEvent) => {
      if (isInvalidForCheckbox) return;
      e.stopPropagation();
      changeCell(!enabled);
    },
    [isInvalidForCheckbox, changeCell, enabled]
  );

  const handleLabelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isInvalidForCheckbox || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      e.stopPropagation();
      changeCell(!enabled);
    },
    [isInvalidForCheckbox, changeCell, enabled]
  );

  const CellValueFormatted = useMemo(
    () =>
      formatNumbers({
        format: cellFormat,
        prefix: cellPrefix ?? null,
        suffix: cellSuffix,
        allowNegativeValue: cellAllowNegativeValue,
        currency: cellCurrency,
        currencyCode: cellCurrencyCode,
        blankIfNull: cellBlankIfNull,
        staticCurrency: cellStaticCurrency,
        value: cellValue,
      }),

    [
      cellAllowNegativeValue,
      cellBlankIfNull,
      cellCurrency,
      cellCurrencyCode,
      cellFormat,
      cellPrefix,
      cellStaticCurrency,
      cellSuffix,
      cellValue,
    ]
  );

  const numberDisplay = useMemo(
    () => (
      <Strike classes={classes} enabled={enabled} isValidNumber={!isInvalidForCheckbox} value={CellValueFormatted} />
    ),
    [CellValueFormatted, enabled, classes, isInvalidForCheckbox]
  );

  const wrappingDivStyle: CSSProperties | undefined =
    isEditableTitleCell && !rightAlignContent ? { textAlign: 'left' } : undefined;

  let content;

  // Manual layout - intentionally NOT wrapped in MUI FormControlLabel.
  // FormControlLabel binds clicks on the label (i.e. the entire numberDisplay
  // text area) to the checkbox via htmlFor/id, which caused every click on
  // the amount value (including double-clicks that should open the ValueEditor)
  // to toggle the exclusion checkbox. Rendering Checkbox + numberDisplay as
  // siblings here means only direct clicks on the Checkbox toggle it; clicks
  // anywhere else in the cell bubble up to the datasheet for selection / edit.
  // For valuations multiples, the number label area is clickable (handleLabelClick)
  // so a click on the selected cell's value toggles exclude/include. CFL disables
  // this (toggleOnLabelClick=false), leaving only direct checkbox clicks to toggle.
  const renderFormControl = (control: ControlProps) => (
    <div className={classes.controlLabelRoot}>
      {labelTogglesCheckbox ? (
        <span
          className={classes.label}
          onClick={handleLabelClick}
          onKeyDown={handleLabelKeyDown}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}>
          {numberDisplay}
        </span>
      ) : (
        <span className={classes.label}>{numberDisplay}</span>
      )}
      {control}
    </div>
  );
  if (isInvalidForCheckbox) {
    content = selected ? (
      renderFormControl(<span style={{ paddingRight: '2.2rem' }} />)
    ) : (
      <span className="unselected-checkbox-cell">-</span>
    );
  } else {
    content = selected ? (
      renderFormControl(<Checkbox checked={enabled} onChange={handleChange} />)
    ) : (
      <span className="unselected-checkbox-cell">{numberDisplay}</span>
    );
  }
  return <div style={wrappingDivStyle}>{content}</div>;
};

export default NumberMultipleCheckbox;
